import React, { useState, useEffect } from "react";
import {
  makeStyles,
  Paper,
  Grid,
  TextField,
  Button,
  Typography,
  Card,
  CardContent,
  CircularProgress,
  Box,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider
} from "@material-ui/core";
import {
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Refresh as RefreshIcon,
  Phone as PhoneIcon,
  Edit as EditIcon,
  Cancel as CancelIcon
} from "@material-ui/icons";
import { toast } from "react-toastify";
import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import api from "../../services/api";

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(3),
    overflowY: "scroll",
    ...theme.scrollbarStyles,
  },
  configCard: {
    marginBottom: theme.spacing(3),
  },
  statusBox: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(2),
    borderRadius: 4,
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  statusSuccess: {
    backgroundColor: theme.palette.success.light,
    color: theme.palette.success.dark,
  },
  statusError: {
    backgroundColor: theme.palette.error.light,
    color: theme.palette.error.dark,
  },
}));

const VapiConfig = () => {
  const classes = useStyles();

  const [apiKey, setApiKey] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [assistantId, setAssistantId] = useState("");
  
  const [testing, setTesting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(false);
  const [hasConfig, setHasConfig] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState(null);

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    try {
      const { data } = await api.get("/api/vapi/config");
      const configExists = !!data.apiKey;
      setHasConfig(configExists);
      setApiKey(data.apiKey ? "••••••••••••••••" : "");
      setPhoneNumber(data.phoneNumber || "");
      setAssistantId(data.assistantId || "");
      setEditing(!configExists);
    } catch (error) {
      console.error("Error loading config:", error);
      setEditing(true);
    }
  };

  const handleSaveConfig = async () => {
    try {
      setSaving(true);

      await api.post("/api/vapi/config", {
        apiKey: apiKey.includes("•") ? undefined : apiKey,
        phoneNumber,
        assistantId
      });

      toast.success("Configuración guardada exitosamente");
      setEditing(false);
      loadConfig();
    } catch (error) {
      console.error("Error saving config:", error);
      toast.error("Error al guardar la configuración");
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = () => {
    setEditing(true);
    setApiKey("");
  };

  const handleCancelEdit = () => {
    setEditing(false);
    loadConfig();
  };

  const handleTestConnection = async () => {
    try {
      setTesting(true);
      setConnectionStatus(null);

      const { data } = await api.post("/api/vapi/config/test");

      setConnectionStatus({
        success: data.success,
        message: data.message
      });

      if (data.success) {
        toast.success("Conexión exitosa con Vapi.ai");
      } else {
        toast.error(data.message);
      }
    } catch (error) {
      console.error("Error testing connection:", error);
      setConnectionStatus({
        success: false,
        message: error.response?.data?.error || "Error al probar la conexión"
      });
      toast.error("Error al probar la conexión");
    } finally {
      setTesting(false);
    }
  };

  return (
    <MainContainer>
      <MainHeader>
        <Title>Configuración de Vapi.ai</Title>
        <MainHeaderButtonsWrapper>
          {!editing && hasConfig && (
            <Button
              variant="outlined"
              color="primary"
              onClick={handleEdit}
              startIcon={<EditIcon />}
              style={{ marginRight: 8 }}
            >
              Editar
            </Button>
          )}
          {editing && hasConfig && (
            <Button
              variant="outlined"
              onClick={handleCancelEdit}
              startIcon={<CancelIcon />}
              style={{ marginRight: 8 }}
            >
              Cancelar
            </Button>
          )}
          <Button
            variant="outlined"
            color="primary"
            onClick={handleTestConnection}
            disabled={testing || editing}
            startIcon={testing ? <CircularProgress size={20} /> : <RefreshIcon />}
            style={{ marginRight: 8 }}
          >
            Probar Conexión
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSaveConfig}
            disabled={saving || !editing}
            startIcon={saving ? <CircularProgress size={20} /> : null}
          >
            Guardar
          </Button>
        </MainHeaderButtonsWrapper>
      </MainHeader>
      <Paper className={classes.mainPaper} variant="outlined">
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card className={classes.configCard}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Credenciales de Vapi.ai
                </Typography>
                <Typography variant="body2" color="textSecondary" paragraph>
                  Configura tus credenciales de Vapi.ai para habilitar llamadas con IA.
                </Typography>

                <TextField
                  fullWidth
                  variant="outlined"
                  label="API Key"
                  value={apiKey}
                  onChange={(e) => setApiKey(e.target.value)}
                  margin="normal"
                  type="password"
                  helperText="Tu API Key privada de Vapi.ai"
                  disabled={!editing}
                  placeholder={!editing && hasConfig ? "••••••••••••••••" : ""}
                />

                <TextField
                  fullWidth
                  variant="outlined"
                  label="Número de Teléfono"
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value)}
                  margin="normal"
                  placeholder="+1234567890"
                  helperText="Número de teléfono de Vapi.ai (formato E.164)"
                  disabled={!editing}
                />

                <TextField
                  fullWidth
                  variant="outlined"
                  label="Assistant ID (Opcional)"
                  value={assistantId}
                  onChange={(e) => setAssistantId(e.target.value)}
                  margin="normal"
                  helperText="ID del asistente predeterminado de Vapi.ai"
                  disabled={!editing}
                />

                {connectionStatus && (
                  <Box
                    className={`${classes.statusBox} ${
                      connectionStatus.success
                        ? classes.statusSuccess
                        : classes.statusError
                    }`}
                  >
                    {connectionStatus.success ? (
                      <CheckCircleIcon />
                    ) : (
                      <ErrorIcon />
                    )}
                    <Typography variant="body2">
                      {connectionStatus.message}
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Instrucciones de Configuración
                </Typography>
                <Typography variant="body2" paragraph>
                  Para obtener tus credenciales de Vapi.ai:
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemText
                      primary="1. Crea una cuenta en vapi.ai"
                      secondary="Visita https://vapi.ai y regístrate"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="2. Obtén tu API Key"
                      secondary="En el dashboard, ve a Settings > API Keys"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="3. Compra un número de teléfono"
                      secondary="En Phone Numbers, compra un número para llamadas salientes"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="4. Crea un asistente (opcional)"
                      secondary="En Assistants, crea y configura tu asistente de IA"
                    />
                  </ListItem>
                </List>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  ¿Qué es Vapi.ai?
                </Typography>
                <Typography variant="body2" paragraph>
                  Vapi.ai es una plataforma de Voice AI que proporciona infraestructura completa para llamadas con IA:
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircleIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText
                      primary="Telefonía integrada"
                      secondary="No necesitas configurar Twilio o Telnyx"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircleIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText
                      primary="Latencia ultra-baja"
                      secondary="~800ms para conversaciones naturales"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircleIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText
                      primary="STT + LLM + TTS integrados"
                      secondary="Todo en una sola plataforma"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircleIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText
                      primary="Interrupciones naturales"
                      secondary="El usuario puede interrumpir al agente"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircleIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText
                      primary="Function calling"
                      secondary="El agente puede ejecutar acciones"
                    />
                  </ListItem>
                </List>
              </CardContent>
            </Card>

            <Card style={{ marginTop: 16 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Estado del Sistema
                </Typography>
                <List>
                  <ListItem>
                    <ListItemText
                      primary="Vapi.ai API"
                      secondary={connectionStatus?.success ? "Conectado" : "No conectado"}
                    />
                    {connectionStatus?.success ? (
                      <CheckCircleIcon color="primary" />
                    ) : (
                      <ErrorIcon color="disabled" />
                    )}
                  </ListItem>
                  <Divider />
                  <ListItem>
                    <ListItemText
                      primary="Configuración"
                      secondary={apiKey && phoneNumber ? "Completa" : "Incompleta"}
                    />
                    {apiKey && phoneNumber ? (
                      <CheckCircleIcon color="primary" />
                    ) : (
                      <ErrorIcon color="disabled" />
                    )}
                  </ListItem>
                </List>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Paper>
    </MainContainer>
  );
};

export default VapiConfig;
